import { COLORS_DEFAULT } from "@blocknote/core";

/**
 * Parses a CSS colour string (`rgb(...)`, `rgba(...)`, `#RRGGBB`, `#RGB`) into
 * an upper-case `#RRGGBB` hex string. Returns `null` for anything else (named
 * colours, hsl, garbage, etc.).
 *
 * The DOCX/PDF exporters use the returned `#RRGGBB` form directly: the docx
 * library expects 6-char hex (the exporter strips the leading `#`), and the
 * react-pdf renderer accepts the same string as a CSS colour.
 */
export function parseCssColorToHex(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim().toLowerCase();

  const longHex = trimmed.match(/^#([0-9a-f]{6})$/);
  if (longHex) return ("#" + longHex[1]).toUpperCase();

  const shortHex = trimmed.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/);
  if (shortHex) {
    const [, r, g, b] = shortHex;
    return ("#" + r + r + g + g + b + b).toUpperCase();
  }

  const rgbMatch = trimmed.match(
    /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*[\d.]+\s*)?\)$/,
  );
  if (rgbMatch) {
    const r = Number(rgbMatch[1]);
    const g = Number(rgbMatch[2]);
    const b = Number(rgbMatch[3]);
    if (r > 255 || g > 255 || b > 255) return null;
    const hex = [r, g, b].map((n) => n.toString(16).padStart(2, "0")).join("");
    return ("#" + hex).toUpperCase();
  }

  return null;
}

/**
 * Wraps `COLORS_DEFAULT` so the BlockNote DOCX/PDF exporters can render any
 * parseable CSS colour, not only the 9 named palette entries. When a key isn't
 * one of the named entries, we synthesise `{ background, text }` from the
 * parsed hex so the exporter's `colors[key].background` / `.text` lookups
 * succeed and preserve user-visible colour from the editor.
 *
 * Unparseable values still need to be filtered out by `sanitizeBlockColors`
 * before reaching the exporter — this proxy returns `undefined` for those.
 */
export function colorsWithCssFallback(): typeof COLORS_DEFAULT {
  return new Proxy(COLORS_DEFAULT, {
    get(target, key) {
      if (typeof key !== "string") return undefined;
      if (key in target) return target[key as keyof typeof target];
      const hex = parseCssColorToHex(key);
      if (!hex) return undefined;
      return { background: hex, text: hex };
    },
  }) as typeof COLORS_DEFAULT;
}
